import {
  Box, IconButton, Drawer, List, ListItemButton,
  ListItemText, Typography, Avatar, Menu, MenuItem, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon from '@mui/icons-material/Logout';
import { Outlet, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useContext, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
// Montserrat empaquetada, sin pedir nada a Google (27-10-2026: se sacaron los
// @import de fonts.googleapis.com de las páginas). 500, 800 y 900 se usan en
// las pantallas y antes llegaban solo por ese @import.
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/800.css';
import '@fontsource/montserrat/900.css';
import logo from '../assets/images/logo.png';
import NotificationsBell from './NotificationsBell';

const font = 'Montserrat, sans-serif';

// ── Links por rol ──────────────────────────────────────────────────────────────
const getNavLinks = (role) => {
  const common = [
    { label: 'Servicios',      to: '/?scroll=servicios' },
    { label: '¿Por qué Fixo?', to: '/?scroll=porque' },
    { label: 'Cómo funciona',  to: '/?scroll=comofunciona' },
  ];

  const byRole = {
    admin: [
      { label: 'Dashboard',   to: '/admin/dashboard' },
      { label: 'Usuarios',    to: '/admin/users' },
      { label: 'Solicitudes', to: '/admin/requests' },
      { label: 'Prestadores', to: '/admin/prestadores' },
      { label: 'Formularios', to: '/admin/formularios' },
      { label: 'Reclamos',    to: '/admin/reclamos' },
      { label: 'Mi perfil',   to: '/perfil' },
    ],
    user: [
      { label: 'Mis solicitudes', to: '/mis-solicitudes' },
      { label: 'Mi perfil',       to: '/perfil' },
    ],
    prestador: [
      { label: 'Solicitudes',  to: '/prestador/solicitudes' },
      { label: 'Presupuestos', to: '/prestador/presupuestos' },
      { label: 'Agenda',       to: '/prestador/agenda' },
      { label: 'Mi perfil',    to: '/prestador/perfil' },
    ],
  };

  // Admin ve solo sus links sin los de la landing
  if (role === 'admin') return byRole.admin;
  // Otros roles: links comunes + los del rol
  return [...common, ...(byRole[role] || [])];
};

// ── Estilos globales del Layout ────────────────────────────────────────────────
const layoutStyles = `
  .fixo-nav-link-inner {
    font-size: 11px;
    font-weight: 600;
    color: rgba(255,255,255,0.55);
    text-decoration: none;
    font-family: Montserrat, sans-serif;
    transition: color 0.15s;
    cursor: pointer;
  }
  .fixo-nav-link-inner:hover { color: #E7A432; }
`;

// ─────────────────────────────────────────────────────────────────────────────
const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl]     = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Inyectar estilos una sola vez
  if (!document.getElementById('fixo-layout-styles')) {
    const style = document.createElement('style');
    style.id = 'fixo-layout-styles';
    style.innerHTML = layoutStyles;
    document.head.appendChild(style);
  }

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/');
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const navLinks = getNavLinks(user?.role);

  // ── Drawer mobile ────────────────────────────────────────────────────────────
  const drawer = (
    <Box sx={{ width: 260, fontFamily: font, pt: 1 }} onClick={() => setMobileOpen(false)}>

      {/* Header del drawer */}
      <Box sx={{
        px: 2.5, py: 2, mb: 1,
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{
          width: 36, height: 36, bgcolor: '#E7A432',
          color: '#163244', fontSize: 13, fontWeight: 700,
        }}>
          {initials}
        </Avatar>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#163244', fontFamily: font }}>
            {user?.name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#7a93a4', fontFamily: font }}>
            {user?.email}
          </Typography>
        </Box>
      </Box>

      {/* Links de navegación */}
      <List disablePadding>
        {navLinks.map(({ label, to }) => (
          <ListItemButton
            key={to}
            component={RouterLink}
            to={to}
            sx={{ px: 2.5, py: 1.2, '&:hover': { bgcolor: 'rgba(231,164,50,0.08)' } }}
          >
            <ListItemText
              primary={label}
              primaryTypographyProps={{ fontFamily: font, fontWeight: 500, fontSize: 13, color: '#163244' }}
            />
          </ListItemButton>
        ))}

        <ListItemButton
          component={RouterLink}
          to="/perfil"
          sx={{ px: 2.5, py: 1.2, '&:hover': { bgcolor: 'rgba(231,164,50,0.08)' } }}
        >
          <ListItemText
            primary="Mi perfil"
            primaryTypographyProps={{ fontFamily: font, fontWeight: 500, fontSize: 13, color: '#163244' }}
          />
        </ListItemButton>

        <Divider sx={{ my: 1 }} />

        <ListItemButton
          onClick={handleLogout}
          sx={{ px: 2.5, py: 1.2, '&:hover': { bgcolor: 'rgba(192,57,43,0.06)' } }}
        >
          <ListItemText
            primary="Cerrar sesión"
            primaryTypographyProps={{ fontFamily: font, fontWeight: 500, fontSize: 13, color: '#c0392b' }}
          />
        </ListItemButton>
      </List>
    </Box>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── NAVBAR ──────────────────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200,
          bgcolor: '#163244',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 2, md: 5 },
          height: 60,
        }}
      >
        {/* Izquierda: hamburger (mobile) + logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            sx={{ display: { xs: 'flex', md: 'none' }, color: '#fff', p: 0.5 }}
            onClick={() => setMobileOpen(true)}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex', alignItems: 'center', textDecoration: 'none',
              transition: 'transform 0.15s, opacity 0.15s',
              '&:hover': { transform: 'scale(0.96)', opacity: 0.85 },
              '&:active': { transform: 'scale(0.92)' },
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Fixo — inicio"
              sx={{ height: 32, objectFit: 'contain' }}
            />
          </Box>
        </Box>

        {/* Centro: links de navegación (desktop) */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
          {navLinks.map(({ label, to }) => (
            <RouterLink
              key={to}
              to={to}
              className="fixo-nav-link-inner"
              style={{ textDecoration: 'none' }}
            >
              {label}
            </RouterLink>
          ))}
        </Box>

        {/* Derecha: nombre + campana + avatar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{
            color: 'rgba(255,255,255,0.5)',
            fontFamily: font, fontWeight: 500, fontSize: 12,
            display: { xs: 'none', sm: 'block' },
          }}>
            {user?.name}
          </Typography>

          <NotificationsBell />

          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
            <Avatar sx={{
              width: 34, height: 34,
              bgcolor: '#E7A432', color: '#163244',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}>
              {initials}
            </Avatar>
          </IconButton>
        </Box>
      </Box>

      {/* ── MENÚ AVATAR ─────────────────────────────────────────────────────── */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1, minWidth: 220, borderRadius: '14px',
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header del menú avatar */}
        <Box sx={{ px: 2.5, py: 1.8, bgcolor: '#163244' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: font }}>
            {user?.name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: font }}>
            {user?.email}
          </Typography>
        </Box>

        <MenuItem
          onClick={() => { navigate('/perfil'); setAnchorEl(null); }}
          sx={{ gap: 1.5, py: 1.3, px: 2.5, '&:hover': { bgcolor: 'rgba(231,164,50,0.08)' } }}
        >
          <PersonIcon fontSize="small" sx={{ color: '#163244' }} />
          <Typography sx={{ fontSize: 13, fontFamily: font, color: '#163244' }}>Mi perfil</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => { navigate('/perfil'); setAnchorEl(null); }}
          sx={{ gap: 1.5, py: 1.3, px: 2.5, '&:hover': { bgcolor: 'rgba(231,164,50,0.08)' } }}
        >
          <LockResetIcon fontSize="small" sx={{ color: '#163244' }} />
          <Typography sx={{ fontSize: 13, fontFamily: font, color: '#163244' }}>Cambiar contraseña</Typography>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleLogout}
          sx={{ gap: 1.5, py: 1.3, px: 2.5, '&:hover': { bgcolor: 'rgba(192,57,43,0.06)' } }}
        >
          <LogoutIcon fontSize="small" sx={{ color: '#c0392b' }} />
          <Typography sx={{ fontSize: 13, fontFamily: font, color: '#c0392b' }}>Cerrar sesión</Typography>
        </MenuItem>
      </Menu>

      {/* ── DRAWER MOBILE ───────────────────────────────────────────────────── */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' } }}
        PaperProps={{ sx: { borderRadius: '0 16px 16px 0' } }}
      >
        {drawer}
      </Drawer>

      {/* ── CONTENIDO ───────────────────────────────────────────────────────── */}
      <Box sx={{ pt: '60px', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <Outlet />
      </Box>

      {/* ── FOOTER LEGAL ────────────────────────────────────────────────────── */}
      <Box component="footer" sx={{
        bgcolor: '#0f2233',
        px: { xs: 3, md: 6 }, py: 2,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 1.5,
      }}>
        <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: font }}>
          © 2026 Fixo — Todos los derechos reservados
        </Box>
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
          {[
            { label: 'Términos y Condiciones', to: '/terminos' },
            { label: 'Privacidad',             to: '/privacidad' },
            { label: 'Cookies',                to: '/cookies' },
            { label: 'Accesibilidad',          to: '/accesibilidad' },
            { label: 'Comunidad',              to: '/comunidad' },
          ].map(({ label, to }) => (
            <RouterLink
              key={to}
              to={to}
              style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.3)',
                fontFamily: font,
                textDecoration: 'none',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.target.style.color = '#E7A432'}
              onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.3)'}
            >
              {label}
            </RouterLink>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default Layout;
